import { CircleAlert, TriangleAlert } from "lucide-react";
import type { BudgetLevel } from "@/lib/dashboard";
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";

const BAR: Record<BudgetLevel, string> = {
  ok: "bg-accent",
  warning: "bg-warning-mark",
  over: "bg-danger-mark",
};

/** Barra de progresso de orçamento; a cor segue o nível (o texto do estado fica com quem usa). */
export function BudgetBar({ label, percent, level }: { label: string; percent: number; level: BudgetLevel }) {
  const pct = Math.round(percent);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(pct, 100)}
      aria-valuetext={`${pct}% usado`}
      className="h-2 overflow-hidden rounded-full bg-surface-2"
    >
      <div
        className={cn("h-full rounded-full", BAR[level])}
        style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
      />
    </div>
  );
}

/** Orçamento de uma categoria: barra + rótulo em texto (Atenção/Estourou) — o estado nunca depende só da cor. */
export function BudgetProgress({
  name,
  spentMinor,
  budgetedMinor,
  percentUsed,
  level,
  currency,
}: {
  name: string;
  spentMinor: number;
  budgetedMinor: number;
  percentUsed: number;
  level: BudgetLevel;
  currency?: string;
}) {
  const pct = Math.round(percentUsed);
  const rest = budgetedMinor - spentMinor;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
        <span className="truncate font-medium">{name}</span>
        {level === "over" ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-expense-soft px-2 py-0.5 text-xs font-semibold text-expense">
            <CircleAlert aria-hidden="true" className="size-3" />
            Estourou · {pct}%
          </span>
        ) : level === "warning" ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-xs font-semibold text-warning">
            <TriangleAlert aria-hidden="true" className="size-3" />
            Atenção · {pct}%
          </span>
        ) : (
          <span className="shrink-0 text-xs font-semibold text-muted tabular-nums">{pct}%</span>
        )}
      </div>
      <BudgetBar label={name} percent={percentUsed} level={level} />
      <p className="mt-1.5 text-xs text-muted tabular-nums">
        {formatMoney(spentMinor, currency)} de {formatMoney(budgetedMinor, currency)} ·{" "}
        {rest >= 0 ? `restam ${formatMoney(rest, currency)}` : `${formatMoney(-rest, currency)} acima`}
      </p>
    </div>
  );
}
